import { ColumnDef } from "@tanstack/react-table";
import { LinkArrow, TrashAltIcon } from "assets/icons";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import moment from "moment";
import { useMemo, useState } from "react";
import { ItemsTable, TableItemActionsControl } from "ui";

import { NOTIFICATION_REPETITION_LIST } from "../../constants";
import { NotificationsListItem } from "../../notifications.types";
import { NotificationInfo } from "./components";
import { NotificationTableProps } from "./NotificationTable.types";

import styles from "./NotificationTable.module.scss";

const NotificationTable = ({
  notificationsList,
  sorting,
  setSorting,
  isLoading,
  onDeleteNotification,
  onShowProducts
}: NotificationTableProps) => {
  const { currentLanguage } = useLanguage();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const getRepetitionType = (value?: string) => {
    return NOTIFICATION_REPETITION_LIST.find((type) => type.value === value)
      ?.label;
  };

  const columns: Array<
    ColumnDef<NotificationsListItem, NotificationsListItem[]>
  > = useMemo(
    () => [
      {
        id: "Title",
        accessorKey: "Title",
        header: t("Notification"),
        size: 27,
        minSize: 27,
        cell: ({ row }) => (
          <NotificationInfo {...notificationsList[row.index]} />
        )
      },
      {
        id: "PostingDate",
        accessorKey: "PostingDate",
        header: t("Post Date"),
        size: 16,
        minSize: 16,
        cell: ({ row }) => (
          <pre>
            {`${moment(row.original.postingDate).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(row.original.postingDate).format("HH:mm")}`}
          </pre>
        )
      },
      {
        id: "Age",
        accessorKey: "Age",
        header: t("Age"),
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <div>
            {row.original.ageFrom
              ? `${row.original.ageFrom}-${row.original.ageTo}`
              : t("All")}
          </div>
        )
      },
      {
        header: t("Gender"),
        size: 7,
        minSize: 7,
        cell: ({ row }) => (
          <div>{`${row.original.targetGender ?? t("All")}`}</div>
        )
      },
      {
        id: "Earnings",
        accessorKey: "Earnings",
        header: t("Earnings"),
        size: 13,
        minSize: 13,
        cell: ({ row }) => (
          <div>
            {row.original.earningsFrom
              ? `${row.original.earningsFrom?.toLocaleString(
                  "en-US"
                )}-${row.original.earningsTo?.toLocaleString("en-US")}`
              : t("Not relevant")}
          </div>
        )
      },
      {
        id: "Repetition",
        accessorKey: "Repetition",
        header: t("Repeat"),
        size: 12,
        minSize: 12,
        cell: ({ row }) => (
          <div>
            {row.original.repetition
              ? `${t("Every")} ${row.original.repetition?.every} ${getRepetitionType(row.original.repetition?.type)}`
              : t("Never")}
          </div>
        )
      },
      {
        id: "Clicks",
        accessorKey: "Clicks",
        header: t("Clicks"),
        size: 8,
        minSize: 8,
        cell: ({ row }) => (
          <div>{row.original.numberOfClicks?.toLocaleString("en-US")}</div>
        )
      },
      {
        accessorKey: "Link",
        header: () => null,
        size: 6,
        minSize: 6,
        cell: ({ row }) => {
          if (
            !row.original.redirection?.productVariants.length &&
            !row.original.redirection?.productSet
          ) {
            return null;
          }

          return (
            <div
              className={styles.notificationsTable_showProductsBtn}
              onClick={() => {
                if (row.original.redirection?.productVariants) {
                  onShowProducts(row.original);
                }
              }}
            >
              {t("Products")} <LinkArrow />
            </div>
          );
        },
        enableSorting: false
      },
      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 3,
        minSize: 3,
        cell: ({ row }) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Delete",
                Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: () =>
                  onDeleteNotification(notificationsList[row.index].id)
              }
            ]}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ),
        enableSorting: false
      }
    ],
    [notificationsList, openedItemOptionsIndex, currentLanguage]
  );

  return (
    <ItemsTable
      className={styles.notificationsTable}
      columns={columns}
      data={notificationsList}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={65}
    />
  );
};

export default NotificationTable;
